// Warns when a property is already in the system before a new request is made.
//
// Nothing in the request flow used to check this, so the same property could be
// submitted any number of times: 18 addresses in the live database were entered
// twice or more, minutes to weeks apart. 3180 Cuneen Trail went in twice in one
// afternoon, by two different people, typed differently each time.
//
// This is deliberately a WARNING, not a block. A house genuinely relisted months
// later should still go through; the app just has to say what it already knows.

// Street types and directions vary by whoever is typing, and Google Places
// appends the city/state/zip. Reduce both sides to a comparable core.
const STREET_TYPES = {
  trail: "tr", trl: "tr",
  avenue: "ave", av: "ave",
  street: "st",
  road: "rd",
  drive: "dr",
  lane: "ln",
  court: "ct",
  place: "pl",
  boulevard: "blvd",
  circle: "cir",
  parkway: "pkwy",
  highway: "hwy",
  terrace: "ter",
  way: "way",
};
const DIRECTIONS = {
  north: "n", south: "s", east: "e", west: "w",
  northeast: "ne", northwest: "nw", southeast: "se", southwest: "sw",
};

export function normalizeAddress(address) {
  return String(address || "")
    .toLowerCase()
    .replace(/,.*$/, "") // drop city / state / zip
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => STREET_TYPES[word] || DIRECTIONS[word] || word)
    .join(" ")
    .trim();
}

// One address is "the same property" as another when they normalize to the same
// text, or when one is a whole-word prefix of the other. The prefix rule is what
// catches Cuneen: "3180 Cuneen Tr" against "3180 Cuneen Trail E, Inver Grove
// Heights, MN 55076". It stays word-bounded so 3180 never matches 31801.
function sameProperty(a, b) {
  if (!a || !b) return false;
  if (a === b) return true;
  return a.startsWith(`${b} `) || b.startsWith(`${a} `);
}

export function findAddressMatches({
  address,
  deals = [],
  listingLeads = [],
  excludeDealId,
  excludeLeadId,
} = {}) {
  const target = normalizeAddress(address);
  if (!target) return [];

  const isDeleted = (r) => Number(r?.deletedAt) > 0;

  // A file request waiting to be built is stored as a soft-deleted deal, but it
  // is live work, not a deleted record -- and it is the state a property is in
  // right when someone is most likely to request it a second time (1581 Griggs,
  // requested 10:34am and again 10:38am). Keep those; drop real deletions.
  const isPendingFileRequest = (d) =>
    d?.status === "Pending" && d?.requestType !== "saleTurnIn";

  const dealMatches = (deals || [])
    .filter((d) => !isDeleted(d) || isPendingFileRequest(d))
    .filter((d) => String(d?.createdat || "") !== String(excludeDealId || " "))
    .filter((d) => sameProperty(target, normalizeAddress(d?.propertyaddress)))
    .map((d) => ({
      kind: "deal",
      id: String(d.createdat || ""),
      address: d.propertyaddress,
      who: d.agentName || "",
      status:
        d.requestType === "fileRequest" ? "file request" : d.dealStage || "",
      when: Number(d.createdat) || null,
    }));

  const leadMatches = (listingLeads || [])
    .filter((l) => !isDeleted(l))
    // A converted lead has become one of the deals above; reporting both would
    // show the same property to the user twice.
    .filter((l) => l?.status !== "converted")
    .filter((l) => String(l?.id || "") !== String(excludeLeadId || " "))
    .filter((l) => sameProperty(target, normalizeAddress(l?.propertyAddress)))
    .map((l) => ({
      kind: "lead",
      id: String(l.id || ""),
      address: l.propertyAddress,
      who: l.assignedAgentName || l.contactName || "",
      status: l.stage || "",
      when: Number(l.createdAt) || null,
    }));

  return [...dealMatches, ...leadMatches].sort(
    (a, b) => (b.when || 0) - (a.when || 0),
  );
}

// A sentence a person can act on, for the confirmation dialog.
export function describeMatches(matches) {
  return (matches || [])
    .map((m) => {
      const owner = m.who || "someone on the team";
      if (m.status === "file request") {
        return `${m.address} already has a file request in from ${owner}, waiting to be built.`;
      }
      const stage = m.status ? ` (${m.status})` : "";
      return m.kind === "deal"
        ? `${m.address} is already on the Deals Board as ${owner}'s listing${stage}.`
        : `${m.address} is already in the pipeline as ${owner}'s lead${stage}.`;
    })
    .join("\n");
}
